import axios, { AxiosError } from 'libs/axios';
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { usePagination, DEFAULT_PAGE_SIZE } from './usePagination';
import { SortBy, useSort, useFilter } from 'providers';

interface ResourceRequestResponse<T> {
    items: T[];
    countTotal: number;
    countFiltered: number;
}

const getResourceRequest = async <T>(
    url: string,
    page: number,
    pageSize: number,
    sorting: SortBy,
    filters: Record<string, any>
) => {
    const params: any = {
        pagination: {
            page,
            pageSize
        }
    };

    if (sorting) {
        params.sorting = sorting;
    }

    if (filters) {
        params.filter = filters;
    }

    const { data } = await axios.post<ResourceRequestResponse<T>>(url, params);

    return data;
};

export const useResource = <T>({
    id,
    url,
    queryKey,
    enabled = true,
    pageSize = DEFAULT_PAGE_SIZE
}: {
    id: string;
    url: string;
    queryKey: string;
    enabled?: boolean;
    pageSize?: number;
}) => {
    const [page, setPage] = useState(1);

    const { state: sortState } = useSort();
    const { state: filterState } = useFilter();

    const { isError, error, data, isLoading } = useQuery<
        ResourceRequestResponse<T>,
        AxiosError
    >(
        [
            queryKey,
            {
                page,
                sort: sortState?.[id],
                filters: filterState?.[id],
                pageSize
            }
        ],
        () =>
            getResourceRequest<T>(
                url,
                currentPage,
                pageSize,
                sortState?.[id],
                filterState?.[id]
            ),
        {
            keepPreviousData: true,
            enabled,
            onSuccess: (data) => {
                if (data.items.length === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                }
            }
        }
    );

    const { currentPage, pageCount, setCurrentPage } = usePagination(
        data?.countFiltered ?? 0,
        pageSize
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [filterState, setCurrentPage]);

    useEffect(() => {
        setPage(currentPage);
    }, [setPage, currentPage]);

    return {
        isError,
        error,
        items: data?.items ?? [],
        isLoading,
        pageCount,
        currentPage,
        setCurrentPage
    };
};
